import { useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'

import { Numeric, Tooltip } from '@blade/ui-kit'
import { NetworkIcon } from '@root/entities/network/ui'
import { AmountFormatted } from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'

import { PairLink } from '../common'
import { PairLiquidityFormatted } from '../common/pair-liquidity-formatted'
import { PairFeeTooltipContent } from '../pair-fee-tooltip-content'

import type { PairModel } from '../../atoms/reatom-pair'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  pair: PairModel
  onLinkClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
} & ClassNameProps

export const PairSearchItemInfo: React.FC<Props> = ({ pair, onLinkClick }) => {
  const { t } = useTranslation()
  const [volumeUsd] = useAtom(pair.stats['1D'].volumeUsd)
  const [marketCapUsd] = useAtom(pair.marketCapUsd)
  const [fees] = useAtom(pair.fees.totalFees)

  return (
    <Root>
      <FeeColumn>
        <Tooltip content={<PairFeeTooltipContent pair={pair} />}>
          <Fee
            pair={pair}
            onClick={onLinkClick ? (e) => onLinkClick(e) : undefined}
          >
            <Label>F</Label>
            <FeeValueContainer>
              <Value>
                <AmountFormatted amount={fees} />
              </Value>
              <NetworkIcon
                size={10}
                network={pair.network}
                color="var(--text-11)"
              />
            </FeeValueContainer>
          </Fee>
        </Tooltip>
      </FeeColumn>
      <Column
        css={`
          justify-items: start;
        `}
      >
        <VolumeLiquidity>
          <Label>{t('common.volume_short')}</Label>
          <Value>
            <Numeric
              value={volumeUsd}
              preset="currency-amount-short"
              currency="$"
            />
          </Value>
        </VolumeLiquidity>
        <VolumeLiquidity>
          <Label>{t('pool.liquidity-char')}</Label>
          <Value>
            <PairLiquidityFormatted pair={pair} />
          </Value>
        </VolumeLiquidity>
      </Column>
      <McapColumn>
        <ChangeMarketCap
          value={marketCapUsd}
          preset="currency-amount-short"
          currency="$"
        />
      </McapColumn>
    </Root>
  )
}

const Root = styled.div`
  position: relative;
  pointer-events: none;
  display: grid;
  grid-template-columns: minmax(80px, auto) minmax(80px, 1fr) auto;
  gap: 20px;

  height: 40px;
  place-content: end;
`

const Column = styled.div`
  display: grid;
  justify-content: start;
`

const ChangeMarketCap = styled(Numeric)`
  color: var(--text-12);
  font-size: 16px;
  font-weight: 600;
`
const ValueCss = css`
  display: grid;
  grid-template-columns: minmax(20px, auto) auto;
  align-items: center;

  gap: 4px;
`
const Fee = styled(PairLink<PairModel>)`
  pointer-events: auto;
  ${ValueCss}
`
const VolumeLiquidity = styled.span`
  ${ValueCss}
`

const Label = styled.span`
  color: var(--textA-8);

  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  text-transform: uppercase;

  text-align: right;
`

const Value = styled.span`
  color: var(--text-11);

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
`

const FeeValueContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 2px;
`

const FeeColumn = styled(Column)`
  align-items: start;
`

const McapColumn = styled(Column)`
  align-items: center;
  justify-content: end;
  min-width: 6ch;
`
